import './SeveritySegment.scss';
import React from 'react';

import { cn } from 'src/component/ds/utils';
import { SEVERITY_ICON, SEVERITY_STYLE, type AlarmSeverity } from './severity';

export interface SeveritySegmentProps {
  severity: AlarmSeverity;
  count: number;
  /** The client's own criticality label. */
  label: string;
  onClick?: (severity: AlarmSeverity) => void;
}

/**
 * One severity in the breakdown: an icon, the count, the label, and the
 * coloured bar beneath them. The whole button is the click target.
 *
 * @param {SeveritySegmentProps} props see SeveritySegmentProps
 * @returns {JSX.Element} the segment button
 */
export default function SeveritySegment ({ severity, count, label, onClick }: SeveritySegmentProps) {
  const Icon = SEVERITY_ICON[severity];
  const style = SEVERITY_STYLE[severity];

  return (
    <button
      type='button'
      onClick={() => onClick?.(severity)}
      style={{ flexGrow: count }}
      className={cn(
        'alarms-to-triage__segment',
        'ds-flex ds-min-w-[88px] ds-basis-0 ds-flex-col ds-gap-2',
        'ds-border-l ds-border-border',
        'ds-cursor-pointer ds-bg-transparent ds-text-left',
        'focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus'
      )}
      data-testid={`alarms-to-triage-segment-${severity}`}
    >
      <span className='ds-flex ds-min-w-0 ds-items-center ds-gap-2 ds-overflow-hidden ds-pl-2'>
        <Icon aria-hidden='true' sx={{ fontSize: 24 }} className={cn('ds-shrink-0', style.icon)} />
        <span className='ds-shrink-0 ds-text-base ds-font-medium ds-text-foreground'>
          {count}
        </span>
        <span className='alarms-to-triage__label ds-shrink-0 ds-text-base ds-font-medium ds-text-foreground'>
          {label}
        </span>
      </span>
      <span
        className={cn('ds-block ds-h-[14px] ds-w-full ds-rounded-[2px]', style.bar)}
        data-testid={`alarms-to-triage-bar-${severity}`}
      />
    </button>
  );
}
